import { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';

/**
 * Sales figures data for the Home page's global sub-header — ported from the
 * old standalone SalesInfoBar (fixed bar on every page) so the same
 * `/sales-tracker/info-bar-data` + `/sales-tracker/update-dates` polling logic
 * now only powers the Home page's sub-header instead of an independent bar.
 *
 * Always reflects TODAY's real status (no year/month params passed — the
 * backend resolves "today" itself). The old bar used to sync to whatever
 * month someone was browsing on the Sales Tracker page via localStorage;
 * that made the Home page show stale/unrelated months, so it's intentionally
 * not carried over here.
 */
export const useSalesFigures = () => {
  const [salesData, setSalesData] = useState(null);
  const [updateDates, setUpdateDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zeroSalesBar, setZeroSalesBar] = useState(false);
  const [hideSalesBar, setHideSalesBar] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get(`${API}/settings`);
        setZeroSalesBar(response.data?.zero_sales_info_bar || false);
        setHideSalesBar(response.data?.hide_sales_info_bar || false);
      } catch (error) {
        console.error('Error loading sales figures settings:', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const loadSalesData = async () => {
      try {
        setLoading(true);
        const timestamp = Date.now();
        const [salesRes, datesRes] = await Promise.all([
          api.get(`${API}/sales-tracker/info-bar-data?_t=${timestamp}`),
          api.get(`${API}/sales-tracker/update-dates?_t=${timestamp}`),
        ]);
        setSalesData(salesRes.data);
        setUpdateDates(datesRes.data);
      } catch (error) {
        console.error('Error loading sales figures:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSalesData();
    const refreshInterval = setInterval(loadSalesData, 30000);
    const handleVisibilityChange = () => { if (document.visibilityState === 'visible') loadSalesData(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const formatCurrency = (amount) => {
    if (zeroSalesBar) return '£0';
    if (!amount) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const hasData = !loading && salesData && (salesData.entry_date || salesData.day_name);
  let dayName = salesData?.day_name || '';
  if (dayName === 'Thursday') dayName = 'Thur';

  return { loading, hideSalesBar, hasData, salesData, updateDates, dayName, formatCurrency, formatShortDate };
};
